import { Box, Divider, Stack } from '@mui/material';
import { FC, useContext } from 'react';
import Battery3BarIcon from '@mui/icons-material/Battery3Bar';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import { ScorerInfoConfig } from '@/service/types';
import { COMMON_STRING } from '@/constants/dictionary';
import { darkTheme } from '@/contexts/theme/monitoringTool.theme';
import { SPORT_ID } from '@/service/constants';
import { ScoringContext } from '@/contexts/scoring/context';
import { useImageObjectUrl } from '@/service/hooks/useImageObjectUrl';
import { LabelWithData } from '../common/LabelWithData';
import { CustomTooltip } from '../StatsCompareTable/StyledStatsComponents';
import { getAppVersionValue } from './utils';
import { ScorerAvatar } from './ScorerAvatar';

export const getBatteryIcon = (level?: number, isCharging?: boolean) => {
  if (!level) return null;
  const style = {
    transform: 'rotate(+90deg)',
    color:
      level >= 40
        ? darkTheme.palette.indicator[0]
        : level >= 20 && level < 40
        ? darkTheme.palette.iconsColors.yellowCard
        : darkTheme.palette.iconsColors.redCard,
  };

  if (isCharging) {
    return (
      <CustomTooltip title={COMMON_STRING.BATTERY_CHARGING}>
        <BatteryChargingFullIcon color='primary' sx={style} />
      </CustomTooltip>
    );
  }

  return <Battery3BarIcon sx={style} />;
};

export const ScorerInfo: FC<Omit<ScorerInfoConfig, 'roleName' | 'oktaId'>> = ({
  firstName,
  lastName,
  averageFor5LastFixtures,
  numberOfCompletedMatches,
  lastDataCollectionDate,
  applicationInformation,
  scoringApplicationBatteryLevel,
  photo,
  userId,
}) => {
  const imageUrl = useImageObjectUrl(photo);

  const {
    state: { currentSport },
  } = useContext(ScoringContext);

  return (
    <Stack
      direction='row'
      gap={2}
      px={2}
      py={1}
      minWidth='max-content'
      justifyContent='space-around'
      flex='1 0 0'
      border={(theme) => `1px solid ${theme.palette.divider}`}
      divider={<Divider orientation='vertical' flexItem />}
    >
      <Box display='flex' flex='1 0 0'>
        <LabelWithData label={COMMON_STRING.NAME} data={firstName} />
        <ScorerAvatar photoUrl={imageUrl} userId={userId} />
      </Box>
      <LabelWithData label={COMMON_STRING.SURNAME} data={lastName} />
      <LabelWithData
        label={COMMON_STRING.APP_VERSION}
        data={getAppVersionValue(applicationInformation)}
      />
      <LabelWithData
        label={COMMON_STRING.LAST_5_RATINGS}
        data={averageFor5LastFixtures ?? 0}
      />
      <LabelWithData
        label={COMMON_STRING.LAST_MATCH_DATE}
        data={lastDataCollectionDate}
      />
      <LabelWithData
        label={COMMON_STRING.COMPLETED_MATCHES}
        data={numberOfCompletedMatches ?? 0}
      />
      {currentSport?.id !== SPORT_ID.SNOOKER && (
        <LabelWithData
          label={COMMON_STRING.BATTERY_LEVEL}
          data={
            scoringApplicationBatteryLevel?.batteryLevel
              ? `${scoringApplicationBatteryLevel?.batteryLevel}%`
              : COMMON_STRING.NOT_AVAILABLE
          }
          icon={getBatteryIcon(
            scoringApplicationBatteryLevel?.batteryLevel,
            scoringApplicationBatteryLevel?.isCharging,
          )}
        />
      )}
    </Stack>
  );
};
